import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Typography,
  Paper,
  Tooltip,
  Skeleton,
} from '@mui/material';
import { Permission } from '../types';
import {
  groupPermissionsByResource,
  RESOURCE_LABELS,
  ACTION_LABELS,
} from '../hooks/useRoles';

interface PermissionMatrixProps {
  permissions: Permission[];
  selectedPermissionIds: string[];
  onChange: (permissionIds: string[]) => void;
  isLoading?: boolean;
  disabled?: boolean;
  /** Permission IDs that the current user can grant (their own permissions). If not provided, all permissions are grantable. */
  grantablePermissionIds?: string[];
}

const ACTIONS_ORDER = ['read', 'create', 'update', 'delete'];

export const PermissionMatrix: React.FC<PermissionMatrixProps> = ({
  permissions,
  selectedPermissionIds,
  onChange,
  isLoading = false,
  disabled = false,
  grantablePermissionIds,
}) => {
  const groupedPermissions = groupPermissionsByResource(permissions);
  const resources = Object.keys(groupedPermissions).sort();

  // Check if a permission can be granted by the current user
  const canGrantPermission = (permissionId: string): boolean => {
    // If no restriction, all permissions are grantable
    if (!grantablePermissionIds) return true;
    return grantablePermissionIds.includes(permissionId);
  };

  const handleTogglePermission = (permissionId: string) => {
    if (disabled) return;
    if (!canGrantPermission(permissionId)) return;

    const newSelectedIds = selectedPermissionIds.includes(permissionId)
      ? selectedPermissionIds.filter((id) => id !== permissionId)
      : [...selectedPermissionIds, permissionId];

    onChange(newSelectedIds);
  };

  const handleToggleResourceRow = (resource: string) => {
    if (disabled) return;

    const resourcePermissions = groupedPermissions[resource] || [];
    // Only consider grantable permissions for bulk toggle
    const grantableResourcePermissions = resourcePermissions.filter((p) =>
      canGrantPermission(p.id)
    );
    const grantableIds = grantableResourcePermissions.map((p) => p.id);

    // If no grantable permissions, do nothing
    if (grantableIds.length === 0) return;

    const allGrantableSelected = grantableIds.every((id) =>
      selectedPermissionIds.includes(id)
    );

    let newSelectedIds: string[];
    if (allGrantableSelected) {
      // Deselect all grantable permissions for this resource
      newSelectedIds = selectedPermissionIds.filter(
        (id) => !grantableIds.includes(id)
      );
    } else {
      // Select all grantable permissions for this resource
      newSelectedIds = [
        ...selectedPermissionIds.filter((id) => !grantableIds.includes(id)),
        ...grantableIds,
      ];
    }

    onChange(newSelectedIds);
  };

  const handleToggleActionColumn = (action: string) => {
    if (disabled) return;

    const actionPermissions = permissions.filter((p) => p.action === action);
    // Only consider grantable permissions for bulk toggle
    const grantableActionPermissions = actionPermissions.filter((p) =>
      canGrantPermission(p.id)
    );
    const grantableIds = grantableActionPermissions.map((p) => p.id);

    // If no grantable permissions, do nothing
    if (grantableIds.length === 0) return;

    const allGrantableSelected = grantableIds.every((id) =>
      selectedPermissionIds.includes(id)
    );

    let newSelectedIds: string[];
    if (allGrantableSelected) {
      // Deselect all grantable permissions for this action
      newSelectedIds = selectedPermissionIds.filter(
        (id) => !grantableIds.includes(id)
      );
    } else {
      // Select all grantable permissions for this action
      newSelectedIds = [
        ...selectedPermissionIds.filter((id) => !grantableIds.includes(id)),
        ...grantableIds,
      ];
    }

    onChange(newSelectedIds);
  };

  const handleSelectAll = () => {
    if (disabled) return;

    // Only consider grantable permissions for bulk toggle
    const grantablePermissions = permissions.filter((p) =>
      canGrantPermission(p.id)
    );
    const grantableIds = grantablePermissions.map((p) => p.id);

    // If no grantable permissions, do nothing
    if (grantableIds.length === 0) return;

    const allGrantableSelected = grantableIds.every((id) =>
      selectedPermissionIds.includes(id)
    );

    if (allGrantableSelected) {
      // Deselect all grantable permissions (keep non-grantable ones if any were somehow selected)
      onChange(selectedPermissionIds.filter((id) => !grantableIds.includes(id)));
    } else {
      // Select all grantable permissions
      onChange([
        ...selectedPermissionIds.filter((id) => !grantableIds.includes(id)),
        ...grantableIds,
      ]);
    }
  };

  const getPermissionForResourceAction = (resource: string, action: string) => {
    return groupedPermissions[resource]?.find((p) => p.action === action);
  };

  const isResourceFullySelected = (resource: string) => {
    const resourcePermissions = groupedPermissions[resource] || [];
    return resourcePermissions.every((p) => selectedPermissionIds.includes(p.id));
  };

  const isResourcePartiallySelected = (resource: string) => {
    const resourcePermissions = groupedPermissions[resource] || [];
    const selectedCount = resourcePermissions.filter((p) =>
      selectedPermissionIds.includes(p.id)
    ).length;
    return selectedCount > 0 && selectedCount < resourcePermissions.length;
  };

  const isActionFullySelected = (action: string) => {
    const actionPermissions = permissions.filter((p) => p.action === action);
    return actionPermissions.every((p) => selectedPermissionIds.includes(p.id));
  };

  const isActionPartiallySelected = (action: string) => {
    const actionPermissions = permissions.filter((p) => p.action === action);
    const selectedCount = actionPermissions.filter((p) =>
      selectedPermissionIds.includes(p.id)
    ).length;
    return selectedCount > 0 && selectedCount < actionPermissions.length;
  };

  const isAllSelected = () => {
    return permissions.every((p) => selectedPermissionIds.includes(p.id));
  };

  const isPartiallySelected = () => {
    const selectedCount = permissions.filter((p) =>
      selectedPermissionIds.includes(p.id)
    ).length;
    return selectedCount > 0 && selectedCount < permissions.length;
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={300} />
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold', width: 200 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <Tooltip title="Select/Deselect All">
                  <Checkbox
                    checked={isAllSelected()}
                    indeterminate={isPartiallySelected()}
                    onChange={handleSelectAll}
                    disabled={disabled}
                    size="small"
                  />
                </Tooltip>
                <Typography variant="subtitle2">Resource</Typography>
              </Box>
            </TableCell>
            {ACTIONS_ORDER.map((action) => (
              <TableCell key={action} align="center" sx={{ fontWeight: 'bold' }}>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Tooltip title={`Select/Deselect all ${ACTION_LABELS[action]}`}>
                    <Checkbox
                      checked={isActionFullySelected(action)}
                      indeterminate={isActionPartiallySelected(action)}
                      onChange={() => handleToggleActionColumn(action)}
                      disabled={disabled}
                      size="small"
                    />
                  </Tooltip>
                  <Typography variant="caption">
                    {ACTION_LABELS[action]}
                  </Typography>
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {resources.map((resource) => (
            <TableRow key={resource} hover>
              <TableCell>
                <Box display="flex" alignItems="center" gap={1}>
                  <Tooltip title={`Select/Deselect all ${RESOURCE_LABELS[resource] || resource}`}>
                    <Checkbox
                      checked={isResourceFullySelected(resource)}
                      indeterminate={isResourcePartiallySelected(resource)}
                      onChange={() => handleToggleResourceRow(resource)}
                      disabled={disabled}
                      size="small"
                    />
                  </Tooltip>
                  <Typography variant="body2">
                    {RESOURCE_LABELS[resource] || resource}
                  </Typography>
                </Box>
              </TableCell>
              {ACTIONS_ORDER.map((action) => {
                const permission = getPermissionForResourceAction(resource, action);
                if (!permission) {
                  return (
                    <TableCell key={action} align="center">
                      <Typography variant="caption" color="text.disabled">
                        —
                      </Typography>
                    </TableCell>
                  );
                }
                const isGrantable = canGrantPermission(permission.id);
                const isDisabled = disabled || !isGrantable;
                return (
                  <TableCell key={action} align="center">
                    <Tooltip
                      title={!isGrantable ? "You cannot grant this permission (you don't have it)" : ''}
                      disableHoverListener={isGrantable}
                    >
                      <span>
                        <Checkbox
                          checked={selectedPermissionIds.includes(permission.id)}
                          onChange={() => handleTogglePermission(permission.id)}
                          disabled={isDisabled}
                          size="small"
                          sx={!isGrantable ? { opacity: 0.4 } : undefined}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PermissionMatrix;
